import { useCallback } from 'react';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';
import { SessionMessage, ChatSessionType } from '@demind-inc/core';

import { AssistantChatMessage } from '../../../types';
import { useSuggestRecNow } from '../../../mutations';
import { useAuthContext } from '../../AuthProvider';

import { assistantChatFeedbackAtom } from '../../../recoil';
import { trackEventMixpanel } from '../../../../utils';
import {
  transformAdjustmentCTAToSuggestedBlocks,
  containSuggestedBlocksInMessages,
} from '../../../helpers';
import { ChatState } from '../types';
import { DEFAULT_LOADING_STATUS_TEXT } from '../constants';
import { upperFirst, lowerCase } from 'lodash';

// Convert snake_case to Title Case
// e.g. energy_boosts -> Energy boosts
const transformSnackCase = (text: string) => {
  return upperFirst(lowerCase(text.replace('_', ' ')));
};

export const useRecommendationHandlers = (
  state: ChatState,
  updateState: (updates: Partial<ChatState>) => void,
  addMessage: (message: AssistantChatMessage) => void,
  addAbortController: (controller: AbortController) => void,
  handlePlanChatStreaming?: (text: string) => Promise<string>
) => {
  const { user } = useAuthContext();

  const { suggestRecNow } = useSuggestRecNow();

  const startRecommendationFlow = useCallback(() => {
    const feelingMessage: AssistantChatMessage = {
      messageId: String(state.messages.length + 1),
      role: 'assistant',
      sentAt: new Date().toISOString(),
      title: 'How are you feeling? You can always type to me!',
      content: '',
      ctaButtons: [
        { id: 'feeling_energized', text: 'Energized' },
        { id: 'feeling_tired', text: 'Tired' },
        { id: 'feeling_burnt_out', text: 'Burnt out' },
        { id: 'feeling_overwhelmed', text: 'Overwhelmed' },
        { id: 'feeling_custom', text: 'Let me type…' },
      ],
    };

    addMessage(feelingMessage);
    updateState({
      loading: false,
      loadingStatusText: [],
      currentSessionType: 'recommendation',
      recommendationState: { step: 'feeling' },
    });
  }, [state.messages.length]);

  const handleFeelingSelection = useCallback(
    async (feeling: string, isCustom: boolean = false) => {
      if (isCustom) {
        // User wants to type custom feeling - just update state and wait for text input
        updateState({
          recommendationState: {
            ...state.recommendationState,
            step: 'feeling',
            feeling: 'custom',
            customText: '',
          },
        });
        return;
      }

      // Add user message showing the selected feeling
      const userMessage: AssistantChatMessage = {
        messageId: String(state.messages.length + 1),
        role: 'user',
        sentAt: new Date().toISOString(),
        content: transformSnackCase(feeling),
      };
      addMessage(userMessage);

      // User selected a button - move to next step
      const activityMessage: AssistantChatMessage = {
        messageId: String(state.messages.length + 2),
        role: 'assistant',
        sentAt: new Date().toISOString(),
        title: 'What kind of activity do you want to do? You can always type to me!',
        content: '',
        ctaButtons: [
          { id: 'activity_tasks', text: 'Tasks' },
          { id: 'activity_energy_boosts', text: 'Energy boosts' },
          { id: 'activity_random', text: 'Random' },
          { id: 'activity_custom', text: 'Let me type…' },
        ],
      };

      addMessage(activityMessage);
      updateState({
        recommendationState: {
          step: 'activity',
          feeling,
        },
      });
    },
    [state.messages.length]
  );

  const handleActivitySelection = useCallback(
    async (activity: string, isCustom: boolean = false) => {
      if (isCustom) {
        // User wants to type custom activity - just update state and wait for text input
        updateState({
          recommendationState: {
            ...state.recommendationState,
            step: 'activity',
            activity: 'custom',
            customText: '',
          },
        });
        return;
      }

      // Add user message showing the selected activity
      const userMessage: AssistantChatMessage = {
        messageId: String(state.messages.length + 1),
        role: 'user',
        sentAt: new Date().toISOString(),
        content: transformSnackCase(activity),
      };
      addMessage(userMessage);

      // User selected a button - show final confirmation
      const currentState = state.recommendationState;
      const feeling = currentState?.feeling || 'unknown';

      const finalMessage: AssistantChatMessage = {
        messageId: String(state.messages.length + 2),
        role: 'assistant',
        sentAt: new Date().toISOString(),
        title: `Got it, feeling ${transformSnackCase(feeling).toLowerCase()}, and looking for ${transformSnackCase(activity).toLowerCase()}. Want to add anything else?`,
        content: '',
        ctaButtons: [
          { id: 'final_send', text: 'No, tell me what to do' },
          { id: 'final_custom', text: 'Let me type...' },
        ],
      };

      addMessage(finalMessage);
      updateState({
        recommendationState: {
          step: 'final',
          feeling,
          activity,
        },
      });
    },
    [state.messages.length, state.recommendationState]
  );

  const sendRecommendation = useCallback(
    async (customText?: string) => {
      const abortController = new AbortController();
      addAbortController(abortController);

      try {
        const currentState = state.recommendationState;
        if (!currentState) return;

        if (customText) {
          const userMessage: AssistantChatMessage = {
            messageId: String(state.messages.length + 1),
            role: 'user',
            sentAt: new Date().toISOString(),
            content: customText,
          };
          addMessage(userMessage);
        }
        updateState({
          isUserTurn: false,
          loading: true,
          loadingStatusText: [],
        });

        let userInput = '';

        if (currentState.step === 'feeling' && customText) {
          // User typed directly at feeling step
          userInput = `:FREE_TEXT: ${customText}`;
        } else if (currentState.step === 'activity' && customText) {
          // User typed directly at activity step
          const feeling = currentState.feeling || 'unknown';
          userInput = `:BUTTON: ${feeling} / :FREE_TEXT: ${customText}`;
        } else if (currentState.step === 'final') {
          // Final step with button selections
          const feeling = currentState.feeling || 'unknown';
          const activity = currentState.activity || 'unknown';
          userInput = `:BUTTON: ${feeling}. :BUTTON: Looking for ${activity}.`;

          if (customText) {
            userInput += ` :FREE_TEXT: ${customText}`;
          }
        }

        if (!userInput) return;

        await handlePlanChatStreaming(userInput);
      } catch (error) {
        if (error instanceof Error && error.name === 'CanceledError') {
          return;
        }
        throw error;
      } finally {
        updateState({ isUserTurn: true });
      }
    },
    [
      user,
      state.recommendationState,
      state.chatSessionId,
      suggestRecNow,
      addMessage,
      updateState,
      addAbortController,
    ]
  );

  return {
    startRecommendationFlow,
    handleFeelingSelection,
    handleActivitySelection,
    sendRecommendation,
  };
};
